import * as log from 'loglevel';
import {html, render} from 'lit-html';

import Request from '../request';
import Translate from '../translate';
import ResourceLoader from '../resource-loader';
import Order from '../order/order';
import {CartItemProperties, PREVIEW_STATE, RECOVER_SOURCE, SAVED_SOURCE, ThemeConfig} from '../const';

// option types that pick their value from the option values ( comma separated on multi select )
// INFO: a check on option.values alone would not do, other types keep a values array for their own use
// ( dimension builds the entries of its price formula, quantity-discount its tiers, ai its generated images ),
// and their value is no entry of it. Better would be the option type declaring that its value comes from its values.
const LIST_TYPES = ['select', 'dropdown', 'radio', 'checkbox', 'button-swatch', 'color-swatch', 'img-swatch', 'color-img-swatch', 'font', 'country'];

// file key of an upload field: <name>_<n> ( photo_2 belongs to the option, photo_2_name is an Upload-Lift child key )
export const numbered = (key, name) => key.indexOf(name + '_') === 0 && /^\d+$/.test(key.slice(name.length + 1));

// saved configurations of the identified shopper: snapshot, save, list, open and remove
// INFO: the identity is only taken from the marker of the app embed or a guest save - no request names a customer
// a save always creates a new configuration, a saved configuration is never saved over
class SavedConfigs {
    constructor(productOptions) {
        this.po = productOptions;
        this.path = '/api/customer/configs';
        this.guestPath = '/api/customer/guest';
        this.ordersPath = '/api/customer/order-configs';

        this.configs = []; // last loaded list ( newest first )
        this.orders = []; // last loaded ordered lines of the configuration ( newest first )
        this.tab = 'configs'; // tab of the option: configs | orders ( kept across renders )
        this.ordersState = null; // ordered lines of the tab: null | loading | loaded | failed
        this.saving = false; // a save runs: the save button spins, the add to cart waits
        this.saved = false; // the last save of the option succeeded: it shows where to find the saved product
        this.email = null; // email of a guest save typed on this page - only shown in the saved text, never logged or kept
    }

    // ---- public ( product-options maps these 1:1 ) ----

    list() {
        // account login only: an unidentified shopper has none, a guest identity may only save
        if (!SavedConfigs.account()) {
            this.configs = [];
            return Promise.resolve(this.configs);
        }

        return this._request('GET', '', { query: { config: this.po.config.uuid } }).then((data) => {
            // IMPORTANT: order of the answer is kept - newest first
            this.configs = (Array.isArray(data) ? data : []).map((saved) => this._linked(saved));
            this.po.render();
            return this.configs;
        });
    }

    // ordered lines of this configuration, newest first - account login only, the store turned it on
    // an entry is the saved configuration shape the open reads, identical lines show once under their newest order
    listOrders() {
        if (!SavedConfigs.account()) {
            return Promise.reject(SavedConfigs.error(403, 'not_available'));
        }

        // the answer holds the ordered lines of every configuration - kept for the tab a short while per customer, every configuration of the tab reads it
        const key = SavedConfigs.customerKey(SavedConfigs.identity());
        const cached = SavedConfigs._ordersCached(key);
        const load = cached
            ? Promise.resolve(cached)
            : this._request('GET', '', { base: this.ordersPath, optional: true }).then((data) => {
                  if (Array.isArray(data)) {
                      SavedConfigs._set(SavedConfigs.ordersKey, JSON.stringify({ key: key, at: Date.now(), data: data }));
                  }
                  return data;
              });

        return load.then((data) => {
            const seen = new Set();
            const owned = this._ownedFilter();
            this.orders = (Array.isArray(data) ? data : [])
                // the lines of this configuration
                .filter((line) => line && line.properties && line.properties[CartItemProperties.OPTIONS] === this.po.config.uuid)
                .map((line) => ({
                    lineId: line.lineId,
                    title: line.title || '',
                    orderName: line.orderName || '',
                    orderDate: line.orderDate || null,
                    productHandle: line.handle || null,
                    variantId: line.variantId || null,
                    previewUrl: line.preview || null,
                    configUuid: this.po.config.uuid, // filtered above
                    properties: owned(line.properties || {}),
                    order: true
                }))
                .filter((entry) => {
                    // the same values once, the view states differ with every order
                    const key = JSON.stringify(Object.keys(entry.properties).filter((property) => !PREVIEW_STATE.test(property)).sort().map((property) => [property, entry.properties[property]]));
                    return !seen.has(key) && seen.add(key);
                });
            this.po.render();
            return this.orders;
        });
    }

    // saved: id, list entry, full saved configuration or ordered line - overwrites the current values, nothing is reset first
    async open(saved) {
        if (typeof saved === 'string' || !saved.properties) {
            const id = typeof saved === 'string' ? saved : saved.id;
            // the public id is the secret: no identity asked or sent, a failure only fails this open
            saved = this._linked(await this._request('GET', '/' + encodeURIComponent(id), { anonymous: true }));
        }

        // a saved configuration belongs to the configuration it was made with ( no notice: a link opens on every instance of the page )
        if (saved.configUuid !== this.po.config.uuid) {
            throw SavedConfigs.error(422, 'config_mismatch');
        }

        const skipped = await this._apply(saved.properties || {});

        this.report(skipped);

        return saved;
    }

    remove(saved) {
        const id = typeof saved === 'string' ? saved : saved.id;

        return this._request('DELETE', '/' + encodeURIComponent(id)).then(() => {
            this.configs = this.configs.filter((entry) => entry.id !== id);
            this.po.render();
        });
    }

    // ---- page load ----

    // ?cl_config=<public id> > applied on any instance with a matching configuration ( also without the option )
    // needs no identity and no saved configurations for the shopper - the merchant opens the link of a customer's configuration as well
    // applied > the cart attributes the order to the saved configuration link, whether or not the shopper adds to cart
    async openFromUrl() {
        const id = SavedConfigs.urlParam();
        if (id && await this.pick(id)) {
            this._markCart(SAVED_SOURCE);
        }
    }

    // ?cl_recover=<token> > the newest saved configuration of the token made on a variant of this product, opened like a configuration link
    // needs no identity - nothing to apply or a failure leaves the page as it is, the parameter is dropped either way
    async recoverFromUrl() {
        let items = [];
        try {
            items = await SavedConfigs.recovery(this.po.settings.api, this.po.settings.queryParams.shop, SavedConfigs.recoverParam());
        } catch (error) {
            log.warn('[live-options] recovery link not loaded', error);
        }

        // newest first, of this configuration ( another instance of the page opens its own )
        const variants = (this.po.settings.product.variants || []).map((variant) => String(variant.id));
        const saved = (Array.isArray(items) ? items : []).find((item) => item.configUuid === this.po.config.uuid && variants.indexOf(String(item.variantId)) !== -1);

        // made on another variant > once more on its variant, the theme selects it from the url ( once: the url holds a variant then )
        if (saved && String(saved.variantId) !== String(this.po._variantId) && !/[?&]variant=/.test(window.location.search)) {
            SavedConfigs.navigate(SavedConfigs.returnUrl(saved.variantId));
            return;
        }

        SavedConfigs.replaceUrl(SavedConfigs.withoutParam(SavedConfigs.recoverName));

        // applied > the cart attributes the order to the recovery like the cart recovery, whether or not the shopper adds to cart
        if (saved && await this.pick(saved)) {
            this._markCart(RECOVER_SOURCE);
        }
    }

    // cart attribute `from` like an order restore sets it - not awaited, a failure is only logged
    _markCart(source) {
        this.po.shopifyCart.setAttributes({ from: 'LPO_' + source })
            .catch((error) => log.warn('[live-options] cart attribute not set', error));
    }

    // open from the ui > never stops the shopper
    pick(saved) {
        return this.open(saved).catch((error) => log.warn('[live-options] configuration not opened', error));
    }

    // work stashed before the login / reload > values come back, a pending save goes on
    resume() {
        const stash = SavedConfigs.readStash(this.po.config.uuid, this.po.settings.product.handle);
        if (!stash) {
            return false;
        }

        this._apply(stash.properties).then(() => {
            const identity = SavedConfigs.identity();
            if (stash.pending && SavedConfigs.available() && identity.signature) {
                this.promptSave();
            }
        });

        return true;
    }

    // product url with the variant and the saved configuration > opens the product it was made on with the configuration applied
    link(saved) {
        if (!saved || !saved.id || !saved.productHandle) {
            return null;
        }

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const variant = saved.variantId ? 'variant=' + encodeURIComponent(saved.variantId) + '&' : '';

        return window.location.origin + root + 'products/' + encodeURIComponent(saved.productHandle) + '?' + variant + SavedConfigs.param + '=' + encodeURIComponent(saved.id);
    }

    // list for the option - loaded once ( a rejected identity reloads once only ), the ordered lines when the shopper opens their tab
    load() {
        if (!this._loaded && !this.po.settings.editorMode) {
            this._loaded = true;
            this.list().catch((error) => log.warn('[live-options] saved configurations not loaded', error));
        }
    }

    // tab of the option ( configs | orders ) - the ordered lines load on the first open, again only after a failure
    selectTab(tab) {
        this.tab = tab;
        if (tab === 'orders' && this.ordersState !== 'loading' && this.ordersState !== 'loaded') {
            this.ordersState = 'loading';
            this.listOrders()
                .then(() => { this.ordersState = 'loaded'; }, (error) => {
                    this.ordersState = 'failed';
                    log.warn('[live-options] ordered configurations not loaded', error);
                })
                .then(() => this.po.render());
        }
        this.po.render();
    }

    // save option visible: app embed on, able to sign and not hidden for this visit
    shown() {
        return this.po.settings.editorMode || SavedConfigs.available();
    }

    // list option visible: an account login
    listShown() {
        return this.po.settings.editorMode || (SavedConfigs.available() && SavedConfigs.account());
    }

    // ---- snapshot & apply ----

    // NEW: the form values reduced to the keys the options own + the preview state - the work kept across a login ( see _stash )
    // INFO: the form is the source of the values ( option.value holds the CORS url of an upload and no editor, library or preview state ),
    // but not of the keys - it also carries the properties of other apps and what the submit wrote ( exports, sku, _cl_options )
    snapshot() {
        const po = this.po;

        if (ThemeConfig.previewState) {
            po._savePreviewState();
        }

        return this.owned(po.shopifyStore.getFormProperties(po._productForm));
    }

    // the part of a property map the options of the page apply: the keys they own + the view states
    // IMPORTANT: one rule for every open - a saved configuration and an ordered line arrive as the cart line, the app's keys, prices and exports included
    owned(properties) {
        return this._ownedFilter()(properties);
    }

    // the owned rule built once > a filter for many property maps
    _ownedFilter() {
        const keys = new Set();
        const uploads = []; // an upload field writes one key per file: <name>_1, <name>_2 ...

        // the keys of one name, as setValues and the group-editor restore read them back
        const own = (name, files) => {
            if (!name) {
                return;
            }
            keys.add(name);
            keys.add('_' + name + '_editor'); // text editor settings
            keys.add('_' + name + '_id'); // library image
            if (files) {
                uploads.push(name);
            }
        };
        const upload = (option) => option.type === 'upload' || option.type === 'upload-lift';

        this._each((option) => {
            own(option.name, upload(option));
            if (option.type === 'group-editor') {
                // the instances ( photo_1 ) are options of their own, the template names hold the instances of a saved configuration the page has none of
                (option.options || []).forEach((instance) => own(instance.name, upload(instance)));
                ((option._controller && option._controller.names) || []).forEach((name) => own(name, true));
            }
        });

        return (properties) => {
            const result = {};
            for (const property in properties) {
                if (properties.hasOwnProperty(property) && (keys.has(property) || PREVIEW_STATE.test(property) || uploads.some((name) => numbered(property, name)))) {
                    result[property] = properties[property];
                }
            }
            return result;
        };
    }

    // compare with the live configuration, drop what it does not accept, then setValues > render > preview states > preview
    // INFO: a key no option of the page owns is left out by the key filter, without a report
    async _apply(properties) {
        const po = this.po;
        const values = this.owned(properties);
        const skipped = [];
        const untouched = []; // group-editors the saved configuration holds no entries of

        this._each((option) => {
            if (option.type === 'upload-lift' || option.type === 'upload') {
                const keys = Object.keys(values).filter((key) => key === option.name || numbered(key, option.name));
                // the plain field carries its files on the value, Upload-Lift on the controller
                const held = option.type === 'upload' ? !!option.value : !!(option._controller && option._controller.urls && option._controller.urls.length);
                if (keys.length && held) {
                    // the field only restores when empty > the shopper keeps the own file
                    keys.forEach((key) => delete values[key]);
                    skipped.push({ name: option.name, label: SavedConfigs._label(option), reason: 'file_kept' });
                }
            } else if (option.type === 'group-editor' && option._controller) {
                const controller = option._controller;
                if (controller.names.some((name) => controller._findInstanceValues(values, name).length)) {
                    // the controller ignores every restore after its first
                    controller._restored = false;
                } else {
                    untouched.push(option);
                }
            } else if (LIST_TYPES.indexOf(option.type) !== -1 && option.values && option.values.length && typeof values[option.name] === 'string' && values[option.name]) {
                const value = values[option.name];
                const listed = (part) => option.values.some((optionValue) => String(optionValue.value).trim() === part.trim());
                if (!listed(value)) {
                    const parts = value.split(',');
                    const accepted = parts.filter(listed).join(',');
                    // nothing accepted > the option keeps its value
                    if (accepted) {
                        values[option.name] = accepted;
                    } else {
                        delete values[option.name];
                    }
                    parts.filter((part) => !listed(part)).forEach((part) => {
                        skipped.push({ name: option.name, label: SavedConfigs._label(option), reason: 'value_missing', value: part.trim() });
                    });
                }
            }
        });

        await this._restoreFiles(values, skipped);

        po.setValues(values);
        // a restore clears the entries first > not for a group the saved configuration does not mention
        untouched.forEach((option) => delete option._restoreValues);
        po.render();

        // deferred: a link is opened before this.ready exists
        await Promise.resolve().then(() => po.ready);
        await po._queuePreviewStates(values);
        await po._renderPreview();

        return skipped;
    }

    // saves the current values as a new configuration of the shopper: the cart line as the add to cart builds it, exports included
    // a file that could not be kept is left out of the saved configuration, without a report
    // an option that is not valid ( e.g. an upload still running ) rejects with 422 invalid and shows its error, like the add to cart
    async save() {
        const po = this.po;

        // a save runs: no second save and no second Flow event
        if (this.saving) {
            return null;
        }
        if (!SavedConfigs.available()) {
            throw SavedConfigs.error(403, 'not_available');
        }
        // only a save sends an unidentified shopper to the login
        if (!SavedConfigs.identity().signature) {
            this._stash(true);
            SavedConfigs.navigate(SavedConfigs.loginUrl(po._variantId));
            throw SavedConfigs.error(401, 'unauthorized');
        }

        // the export takes a while: the button spins and an add to cart meanwhile is cancelled, both released on success and failure
        this.saving = true;
        this.saved = false;
        po._blockCartAdd(true);
        po.render();

        try {
            const properties = await po._captureCartProperties();
            if (!properties) {
                throw SavedConfigs.error(422, 'invalid');
            }

            const input = {
                configUuid: po.config.uuid, // IMPORTANT: from the instance - _cl_options is missing under some theme settings
                productHandle: po.settings.product.handle,
                productId: po.settings.product.id,
                variantId: po._variantId,
                quantity: po._productFormQuantity(),
                properties: SavedConfigs.lineProperties(properties),
                // the capture exported the thumbnail already ( export on ), else one of the active preview
                previewUrl: properties[CartItemProperties.PREVIEW] || await this._capturePreview(),
                // one token groups the saves of the tab, the server answers the one to keep
                token: SavedConfigs._get(SavedConfigs.tokenKey)
            };

            const answer = await this._request('POST', '', { data: input, pending: true });
            const result = answer.configs[0];
            if (answer.token) {
                SavedConfigs._set(SavedConfigs.tokenKey, answer.token);
            }

            // newest first
            this.configs.unshift(this._linked(result));
            this.saved = true;

            return result;
        } finally {
            this.saving = false;
            po._blockCartAdd(false);
            po.render();
        }
    }

    // save for later of several cart lines in one request: every item is a ConfigBody, all or nothing - resolves { token, configs } in item order
    // the items join the recovery token of the tab, the answered one is kept
    async saveBatch(items) {
        const answer = await this._request('POST', '/batch', { data: { token: SavedConfigs._get(SavedConfigs.tokenKey), items: items } });
        if (answer.token) {
            SavedConfigs._set(SavedConfigs.tokenKey, answer.token);
        }

        return answer;
    }

    // thumbnail of the active preview - optional ( no live preview or preview not visible )
    async _capturePreview() {
        const preview = this.po._preview;

        try {
            if (preview && typeof preview.export === 'function') {
                const config = preview.previewConfig;
                const width = (config.exportConfig && config.exportConfig.thumbnailWidth) || 500;
                const height = (config.exportConfig && config.exportConfig.thumbnailHeight) || Math.round((width * config.height) / config.width) || width;

                const data = preview.export({ width: width, height: height }, false, true, 'excludeThumbnail', 'png', 0.8);
                if (data) {
                    const fileMeta = this.po._upload._fileMeta.fromBlob(data, 'config_preview.png');
                    fileMeta.meta.variant = 'thumbnail';

                    return (await this.po._uploadPreview(fileMeta)).url;
                }
            }
        } catch (error) {
            log.warn('[live-options] configuration preview failed', error);
        }

        return null;
    }

    // leaf options - a group-editor counts as one ( restores its own instances )
    _each(callback, options = this.po.config.options) {
        for (let i = 0; i < options.length; i++) {
            const option = options[i];
            if (option.options && option.options.length && option.type !== 'group-editor') {
                this._each(callback, option.options);
            } else {
                callback(option);
            }
        }
    }

    // the files of the configuration back on the storage before setValues, one request for every field ( plain upload, Upload-Lift, group-editor instances ):
    // a held working copy is found as it is, a file of an ordered line is copied back from the archive to its link
    // a gone file ( e.g. removed by the retention ) is left out and reported, a failed request keeps the links as saved
    // INFO: runs on every open - a condition `saved.order || saved.orderedDate` would skip the files that are still held, but the order archive releases
    // the hold per file: a file one configuration was ordered with can be a file of another saved configuration that was never ordered ( its orderedDate stays empty )
    async _restoreFiles(values, skipped) {
        const keys = Object.keys(values).filter((key) => Order.isFileUrl(values[key]));
        if (!keys.length) {
            return;
        }

        let files;
        try {
            files = await Order.restoreFiles({ api: this.po.settings.api, shop: this.po.settings.queryParams.shop }, keys.map((key) => values[key]), true);
        } catch (error) {
            log.warn('[live-options] files not restored', error);
            return;
        }
        if (!Array.isArray(files)) {
            log.warn('[live-options] files not restored', files);
            return;
        }

        const restored = new Set(files.map((file) => file.url));
        const reported = new Set();
        keys.filter((key) => !restored.has(values[key])).forEach((key) => {
            delete values[key];
            const option = this._fileOption(key);
            const name = option ? option.name : key;
            if (!reported.has(name)) {
                reported.add(name);
                skipped.push({ name: name, label: option ? SavedConfigs._label(option) : key, reason: 'file_missing' });
            }
        });
    }

    // the upload option of a file key ( its name or a numbered file of it ), group-editor instances included
    _fileOption(key, options = this.po.config.options) {
        for (const option of options) {
            if ((option.type === 'upload' || option.type === 'upload-lift') && (option.name === key || numbered(key, option.name))) {
                return option;
            }
            const found = option.options && option.options.length ? this._fileOption(key, option.options) : null;
            if (found) {
                return found;
            }
        }
        return null;
    }

    // the api answers no link > built here
    _linked(saved) {
        if (saved && !saved.openUrl) {
            saved.openUrl = this.link(saved);
        }
        return saved;
    }

    // ---- requests ----

    // opts: query, data, base ( default the configs path ), text ( the answer is a plain string, an error body is still json )
    // pending: a save stashed with the work on a reload, optional: a 403 does not hide the feature ( the ordered lines )
    // anonymous: no identity sent and no rejected identity handling ( the open by public id, the guest save )
    _request(method, path, opts = {}) {
        const identity = SavedConfigs.identity();
        if (!opts.anonymous && !SavedConfigs.available()) {
            return Promise.reject(SavedConfigs.error(403, 'not_available'));
        }

        // IMPORTANT: Request does not encode query values
        const params = Object.assign({}, opts.query);
        if (this.po.settings.queryParams.shop) {
            params.shop = encodeURIComponent(this.po.settings.queryParams.shop);
        }

        const options = { method: method, responseType: opts.text ? 'text' : 'json', headers: {}, queryParams: params };
        if (!opts.anonymous && identity.signature) {
            options.headers.Authorization = 'CL ' + identity.message + '.' + identity.signature;
        }
        if (opts.data) {
            options.headers['Content-type'] = 'application/json';
            options.data = opts.data;
        }

        return new Request(this.po.settings.api + (opts.base || this.path) + path, options)
            .then((response) => {
                // the identity was accepted > a later rejection may reload again
                if (options.headers.Authorization) {
                    SavedConfigs._remove(SavedConfigs.reloadKey);
                }
                return response.data || {};
            })
            .catch((failure) => {
                const body = failure && failure.data && typeof failure.data === 'object' ? failure.data : {};
                const error = SavedConfigs.error(failure && failure.xhr ? failure.xhr.status : (failure && failure.status) || 0, body.error || 'failed', body);
                if (!opts.anonymous) {
                    this._rejected(error, identity, opts);
                }
                throw error;
            });
    }

    // rejected guest identity: dropped, the next save asks for the email again
    // rejected account identity: one reload for a fresh value, then ( or on a 403, blocked storage ) hidden for the visit
    _rejected(error, identity, opts) {
        if (error.status === 401 && identity.order) {
            SavedConfigs.setOrder(null);
            this.po.render();
        } else if (error.status === 401 && identity.signature && SavedConfigs.rejected() && this._stash(opts.pending)) {
            // on the selected variant like the login
            SavedConfigs.replaceUrl(SavedConfigs.returnUrl(this.po._variantId) + window.location.hash);
            SavedConfigs.reload();
        } else if (error.status === 401 || (error.status === 403 && !opts.optional)) {
            SavedConfigs.off();
            this.po.render();
        }
    }

    // false: the work could not be kept
    _stash(pending) {
        // standalone ( the cart ): no work of a page to keep
        if (!this.po.config) {
            return true;
        }

        const snapshot = this.snapshot();

        return SavedConfigs._set(
            SavedConfigs.stashKey,
            JSON.stringify({
                config: this.po.config.uuid,
                handle: this.po.settings.product.handle,
                variant: this.po._variantId,
                ts: new Date().getTime(),
                properties: snapshot,
                pending: pending === true
            })
        );
    }

    // ---- dialogs ( also used without the option: link notice and skipped report ) ----

    // save button: login for an unidentified shopper, then the save - no dialog of its own
    async promptSave() {
        if (this.saving || this.po.settings.editorMode || !SavedConfigs.identity()) {
            return;
        }
        if (!SavedConfigs.identity().signature && !(await this.identify())) {
            return;
        }

        try {
            await this.save();
        } catch (error) {
            this._failed(error);
        }
    }

    // login prompt of an unidentified shopper - true when identified in place ( guest email ), the login page leaves the page
    async identify() {
        const guest = SavedConfigs.guestSave();
        let action = await this._dialog(this._loginContent());
        // the guest part was turned off meanwhile ( 403 ) > the prompt once more without it
        if (action === 'guest' && guest && !SavedConfigs.guestSave() && !SavedConfigs.identity().signature) {
            action = await this._dialog(this._loginContent());
        }
        if (action === 'login') {
            this._stash(true);
            SavedConfigs.navigate(SavedConfigs.loginUrl(this.po._variantId));
        }
        return action === 'guest' && SavedConfigs.available() && !!SavedConfigs.identity().signature;
    }

    // account login, or only the email form where the store turned on the guest save
    _loginContent() {
        if (!SavedConfigs.guestSave()) {
            return { actions: [{ value: 'login', text: SavedConfigs.t('login') }] };
        }

        return {
            title: SavedConfigs.t('login.guest'),
            email: {
                label: SavedConfigs.t('login.guest.email'),
                marketing: SavedConfigs.guestMarketing() ? SavedConfigs.t('login.guest.marketing') : null,
                required: SavedConfigs.t('login.guest.required')
            },
            actions: [{ value: 'guest', text: SavedConfigs.t('login.guest.submit'), run: (values) => this._guestLogin(values) }]
        };
    }

    // creates a customer for the email and keeps its identity for the visit - resolves the text to show, empty when the dialog may close
    async _guestLogin(values) {
        try {
            // purpose save: an email that already belongs to a customer answers an identity that may only save
            SavedConfigs.setOrder(await this._request('POST', '', { data: { app: 'options', email: values.email, marketing: values.marketing, purpose: 'save' }, base: this.guestPath, text: true, anonymous: true }));
        } catch (error) {
            if (error.status === 403) {
                // only the guest save is gone, the saved configurations stay
                SavedConfigs.guestOff = true;
                return '';
            }
        }

        if (!SavedConfigs.identity().order) {
            return SavedConfigs.t('login.guest.error');
        }
        this.email = values.email;
        return '';
    }

    async confirmRemove(saved) {
        const action = await this._dialog({
            title: SavedConfigs.t('remove.confirm'),
            actions: [{ value: 'remove', text: SavedConfigs.t('remove') }]
        });
        if (action) {
            this.remove(saved).catch((error) => log.warn('[live-options] configuration not removed', error));
        }
    }

    // what was not applied when a saved configuration was opened - a kept file of the shopper is not reported ( the event still has it )
    report(skipped) {
        const names = skipped.filter((entry) => entry.reason !== 'file_kept');

        if (names.length) {
            this._dialog({
                title: SavedConfigs.t('skipped'),
                items: names.map((entry) => (entry.label || entry.name) + (entry.value ? ': ' + entry.value : ''))
            });
        }
    }

    notice(text) {
        return this._dialog({ title: text });
    }

    // where the shopper finds a save again: a guest save names the typed email ( set as text, lit escapes it ), an account save the account with its link
    // shared by the save-config option and the cart save
    savedNotice() {
        const guest = !!this.email && !!(SavedConfigs.identity() || {}).order;
        const parts = SavedConfigs.t(guest ? 'saved.guest' : 'saved').split('{email}');

        return html`${parts[0]}${parts.length > 1 ? html`<strong>${this.email}</strong>${parts.slice(1).join(this.email)}` : null} <a href="${SavedConfigs.accountUrl()}">${SavedConfigs.t('saved.link')}</a>`;
    }

    _failed(error) {
        // 401 / 403 already reload or hide the feature, an option that is not valid shows its own error
        if (error.status !== 401 && error.status !== 403 && error.error !== 'invalid') {
            this.notice(SavedConfigs.t('error'));
        }
    }

    // modal of the app ( same classes as the cart item dialog ) - resolves the value of the clicked action, null when closed
    // content: { title, items, email: { label, marketing, required }, actions: [{ value, text, run }] } - run resolves a text to stay open with
    async _dialog({ title, items = [], email = null, actions = [] }) {
        if (typeof HTMLDialogElement !== 'function') {
            await ResourceLoader.loadOnce(
                [this.po.settings.assets + '/static/lib/dialog/dialog-polyfill.min.js', this.po.settings.assets + '/static/lib/dialog/dialog-polyfill.min.css'],
                'dialog',
                { async: false }
            );
        }

        return new Promise((resolve) => {
            const dialogEl = document.createElement('dialog');
            dialogEl.className = 'cl-po--dialog';
            let error = '';
            let busy = false;
            let closed = false;

            const input = (name) => dialogEl.querySelector('[name="cl-config-' + name + '"]');
            const close = (value) => !closed && dialogEl.close(value);

            const submit = async (action) => {
                // Enter while a request runs ( the buttons are disabled )
                if (busy) {
                    return;
                }
                const values = email ? { email: input('email').value.trim(), marketing: !!input('marketing') && input('marketing').checked } : {};
                if (email && !values.email) {
                    error = email.required;
                    view();
                    input('email').focus();
                    return;
                }
                if (action.run) {
                    busy = true;
                    error = '';
                    view();
                    error = await action.run(values);
                    busy = false;
                    view();
                    if (error) {
                        return;
                    }
                }
                close(action.value);
            };

            const enter = (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    submit(actions[0]);
                }
            };

            // lit sets every text as text
            const view = () => render(html`
                <button type="button" class="cl-po--dialog-close" aria-label="${SavedConfigs.t('close', null, 'Close')}" @click=${() => close('')}>×</button>
                ${title ? html`<h4>${title}</h4>` : null}
                ${items.length ? html`<ul>${items.map((item) => html`<li>${item}</li>`)}</ul>` : null}
                ${email ? html`
                    <label class="cl-po--dialog-field">
                        <span>${email.label}</span>
                        <input class="cl-po--input" type="text" inputmode="email" maxlength="254" autocomplete="email" name="cl-config-email" @keydown=${enter}>
                    </label>
                    ${email.marketing ? html`<label class="cl-po--dialog-check"><input type="checkbox" name="cl-config-marketing"> ${email.marketing}</label>` : null}
                ` : null}
                ${error ? html`<div class="cl-po--error" role="alert">${error}</div>` : null}
                ${actions.length ? html`
                    <div class="action">
                        ${actions.map((action) => html`
                            <button type="button" class="cl-po--swatch cl-po--swatch-button cl-po--small" value="${action.value}" ?disabled=${busy} @click=${() => submit(action)}>${action.text}</button>
                        `)}
                    </div>
                ` : null}
            `, dialogEl);

            view();

            // the dialog sits inside the product form > the theme does not see its inputs change
            dialogEl.addEventListener('change', (e) => e.stopPropagation());

            dialogEl.addEventListener('close', () => {
                closed = true;
                dialogEl.parentNode.removeChild(dialogEl);
                resolve(dialogEl.returnValue || null);
            });

            // polyfill requires manual register
            if (window.dialogPolyfill !== undefined) {
                window.dialogPolyfill.registerDialog(dialogEl);
                // safari height fix
                dialogEl.style.height = 'auto';
            }

            // add wrapper (required to call showModal & scoped skin CSS to work)
            this.po._wrapper.appendChild(dialogEl);
            dialogEl.showModal();
            if (email) {
                input('email').focus();
            }
        });
    }

    // ---- identity ( same rules as the Upload-Lift client ) ----

    // saved configurations without a ProductOptions instance ( the cart ): requests, the login prompt and the dialogs
    // settings: { api, assets, shop }, wrapper: element that holds the dialogs, render: called when the feature was hidden
    static standalone(settings, wrapper, render) {
        return new SavedConfigs({
            settings: { api: settings.api, assets: settings.assets, queryParams: { shop: settings.shop }, editorMode: false },
            config: null,
            _variantId: null,
            _wrapper: wrapper,
            render: render || (() => {})
        });
    }

    // the line properties of a saved configuration - the add-on json is written again when the line is restored
    static lineProperties(properties) {
        const lineProperties = Object.assign({}, properties);
        delete lineProperties[CartItemProperties.JSON];
        return lineProperties;
    }

    // the storefront i18n serves the product.options.config keys, a fallback only where a text has no served key
    static t(key, params, fallback) {
        return Translate.get('product.options.config.' + key, params || null, fallback);
    }

    static error(status, code, data) {
        return { status: status, error: code, data: data || {} };
    }

    // marker of the app embed - a guest save identity ( <message>.<signature> ) identifies the shopper of an empty marker ( order: true )
    // IMPORTANT: an account login wins over a guest identity kept from before the login
    static identity() {
        const marker = SavedConfigs._marker();
        const value = (marker && !marker.signature && (SavedConfigs._get(SavedConfigs.orderKey) || SavedConfigs.order)) || '';
        const index = value.lastIndexOf('.');

        return index > 0 && index < value.length - 1
            ? Object.assign(marker, { message: value.slice(0, index), signature: value.slice(index + 1), order: true })
            : marker;
    }

    // marker of the app embed, read once per page ( rendered with the page ) - a copy, identity() extends it
    // INFO: a missing marker is not kept, it may be parsed after the app script
    static _marker() {
        if (!SavedConfigs.markerCache) {
            SavedConfigs.markerCache = SavedConfigs._readMarker();
        }
        return SavedConfigs.markerCache && Object.assign({}, SavedConfigs.markerCache);
    }

    // marker rendered by the app embed: the content { m, s } for a logged in customer, empty with the login url ( data-login ) for everyone else
    // own marker first, the Upload-Lift marker is valid too - no marker = the app embeds are off or have no secret
    static _readMarker() {
        let found = null;

        for (let i = 0; i < SavedConfigs.markerAttributes.length; i++) {
            const el = document.querySelector('script[' + SavedConfigs.markerAttributes[i] + ']');
            if (!el) {
                continue;
            }
            try {
                // an empty marker is valid: nobody is logged in ( m = the signed message, s = its signature )
                const content = el.textContent.trim();
                const parsed = content ? JSON.parse(content) : {};
                const marker = { message: parsed.m, signature: parsed.s };
                const login = el.getAttribute('data-login');
                if (login) {
                    marker.login = login;
                }
                // only a complete pair is an identity
                if (marker.message && /^[0-9a-f]{64}$/.test(marker.signature || '')) {
                    return marker;
                }
                delete marker.message;
                delete marker.signature;
                found = found || marker;
            } catch (e) {}
        }

        return found;
    }

    // customer setting of the store turned on ( the key and customer may be absent )
    static setting(key) {
        return window.Cloudlift?.options?.config?.customer?.[key] === true;
    }

    // guest save, not turned off for this page by a 403
    static guestSave() {
        return SavedConfigs.setting('guestSave') && !SavedConfigs.guestOff;
    }

    // guest save asks for the email marketing consent
    static guestMarketing() {
        return SavedConfigs.setting('guestMarketing');
    }

    // logged in with the customer account ( not a guest identity )
    static account() {
        const identity = SavedConfigs.identity();
        return !!identity && !!identity.signature && !identity.order;
    }

    // identity of a guest save: kept for the tab, for the page when the storage is blocked - empty drops it
    static setOrder(value) {
        value = typeof value === 'string' ? value : null;
        SavedConfigs.order = value;
        if (value) {
            SavedConfigs._set(SavedConfigs.orderKey, value);
        } else {
            SavedConfigs._remove(SavedConfigs.orderKey);
        }
    }

    // feature available on this page (embed on with a secret and not hidden for this visit)
    static available() {
        const identity = SavedConfigs.identity();
        return identity !== null && !SavedConfigs.hidden && SavedConfigs._get(SavedConfigs.offKey) === null;
    }

    // hide the feature for the rest of the visit ( for this page when the storage is blocked )
    static off() {
        SavedConfigs.hidden = true;
        SavedConfigs._set(SavedConfigs.offKey, '1');
    }

    // rejected identity: true = first rejection (reload for a fresh value), false = the fresh value was rejected too
    static rejected() {
        if (SavedConfigs._get(SavedConfigs.reloadKey) !== null) {
            SavedConfigs._remove(SavedConfigs.reloadKey);
            return false;
        }
        // IMPORTANT: no reload without the guard - every load would be a first rejection
        return SavedConfigs._set(SavedConfigs.reloadKey, '1');
    }

    // the stash of this configuration and product is cleared on read, a stale one is dropped
    static readStash(config, handle) {
        let stash = null;
        try {
            stash = JSON.parse(SavedConfigs._get(SavedConfigs.stashKey));
        } catch (e) {}

        if (!stash || typeof stash !== 'object') {
            return null;
        }

        const fresh = typeof stash.ts === 'number' && new Date().getTime() - stash.ts <= SavedConfigs.stashMaxAge;
        const match = stash.config === config && stash.handle === handle;

        if (!fresh || match) {
            SavedConfigs._remove(SavedConfigs.stashKey);
        }

        return fresh && match && stash.properties && typeof stash.properties === 'object' ? stash : null;
    }

    static urlParam(name = SavedConfigs.param) {
        const match = new RegExp('[?&]' + name + '=([^&#]*)').exec(window.location.search);
        // a malformed value ( e.g. a cut link ) counts as absent instead of stopping the page load
        try {
            return match ? decodeURIComponent(match[1]) : null;
        } catch (e) {
            return null;
        }
    }

    static recoverParam() {
        return SavedConfigs.urlParam(SavedConfigs.recoverName);
    }

    // saved configurations of a recovery token, newest first - no identity ( the token is the secret )
    static recovery(api, shop, token) {
        // IMPORTANT: Request does not encode query values
        const params = shop ? { shop: encodeURIComponent(shop) } : {};
        return new Request(api + '/api/customer/recovery/' + encodeURIComponent(token), { method: 'GET', responseType: 'json', headers: {}, queryParams: params }).then((response) => response.data);
    }

    // page url without the parameter
    static withoutParam(name) {
        const search = window.location.search.replace(new RegExp('([?&])' + name + '=[^&#]*(&|$)'), '$1').replace(/[?&]$/, '');
        return window.location.pathname + search + window.location.hash;
    }

    static loginUrl(variant) {
        const identity = SavedConfigs.identity();
        const login = identity && identity.login ? identity.login : '/account/login';
        const returnTo = encodeURIComponent(SavedConfigs.returnUrl(variant));
        return login + (login.indexOf('?') === -1 ? '?' : '&') + 'return_to=' + returnTo;
    }

    // customer account of the store ( locale root included ) - the login for a shopper who is not logged in, the page of the saved products is not known here
    static accountUrl() {
        return ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'account';
    }

    // page url with the selected variant - some themes change the variant without the url
    static returnUrl(variant) {
        let search = window.location.search;
        if (variant) {
            search = search.replace(/([?&])variant=[^&#]*(&|$)/, '$1').replace(/[?&]$/, '');
            search += (search ? '&' : '?') + 'variant=' + encodeURIComponent(variant);
        }
        return window.location.pathname + search;
    }

    static navigate(url) {
        window.location.assign(url);
    }

    static replaceUrl(url) {
        try {
            window.history.replaceState(window.history.state, '', url);
        } catch (e) {}
    }

    static reload() {
        window.location.reload();
    }

    static _label(option) {
        return String(option.label || option.name).replace(/(<([^>]+)>)/gi, '').trim() || option.name;
    }

    // INFO: sessionStorage can throw (blocked site data / private mode)
    static _get(key) {
        try {
            return window.sessionStorage.getItem(key);
        } catch (e) {
            return null;
        }
    }

    // false: not kept ( blocked or full )
    static _set(key, value) {
        try {
            window.sessionStorage.setItem(key, value);
            return true;
        } catch (e) {
            return false;
        }
    }

    // shop and customer of a signed identity ( message = v1|app|shop|customerId|companyId|ts|src ) - the same for every page, the signature is not
    static customerKey(identity) {
        const fields = ((identity && identity.message) || '').split('|');
        return fields.length > 3 ? fields[2] + '|' + fields[3] : (identity && identity.signature) || '';
    }

    // raw answer of the ordered lines for this customer while fresh
    static _ordersCached(key) {
        try {
            const entry = JSON.parse(SavedConfigs._get(SavedConfigs.ordersKey));
            return entry && entry.key === key && Date.now() - entry.at < SavedConfigs.ordersMaxAge && Array.isArray(entry.data) ? entry.data : null;
        } catch (e) {
            return null;
        }
    }

    static _remove(key) {
        try {
            window.sessionStorage.removeItem(key);
        } catch (e) {}
    }
}

SavedConfigs.markerAttributes = ['data-live-options-customer', 'data-upload-lift-customer'];
SavedConfigs.markerCache = null; // _marker() of this page
SavedConfigs.param = 'cl_config';
SavedConfigs.recoverName = 'cl_recover';
// recovery token of the tab ( answered by every save for later )
SavedConfigs.tokenKey = 'cl_config_token';
SavedConfigs.stashKey = 'cl_config_stash';
SavedConfigs.reloadKey = 'cl_config_reload';
SavedConfigs.offKey = 'cl_config_off';
// same key as the Upload-Lift client: a guest save in one app identifies the shopper in both
SavedConfigs.orderKey = 'cl_customer_identity';
SavedConfigs.order = null; // setOrder() of this page - the storage key may not be kept
SavedConfigs.stashMaxAge = 30 * 60 * 1000;
SavedConfigs.ordersKey = 'cl_config_orders';
SavedConfigs.ordersMaxAge = 10 * 60 * 1000;
SavedConfigs.hidden = false; // off() of this page - the storage key may not be kept
SavedConfigs.guestOff = false; // guest save answered 403 on this page

export default SavedConfigs;
